import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { trackEvent } from '@/lib/analytics'
import { useSession } from '@/lib/auth-client'
import { useSubscription } from '@/lib/subscription'
import { trpc } from '@/lib/trpc'
import { logger } from '@/lib/utils/logger'
import { loadState, saveState } from './storage'
import { TASKS } from './tasks'
import type { AutoCompleteEvent, OnboardingContextValue, OnboardingState } from './types'

const OnboardingContext = createContext<OnboardingContextValue | undefined>(undefined)

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<OnboardingState>(() => loadState())
  const seededRef = useRef(false)
  const { data: session, isPending: isSessionPending } = useSession()
  const subscription = useSubscription()
  const isAuthenticated = Boolean(session?.user)

  const utils = trpc.useUtils()
  const createNotification = trpc.notifications.create.useMutation({
    onSuccess: () => {
      utils.notifications.list.invalidate()
      utils.notifications.unreadCount.invalidate()
    },
  })

  useEffect(() => {
    saveState(state)
  }, [state])

  // Seed onboarding data on first load
  useEffect(() => {
    if (isSessionPending || !isAuthenticated) return
    if (state.seededAt || seededRef.current) return
    if (createNotification.isPending) return
    seededRef.current = true

    ;(async () => {
      try {
        const notificationResults = await Promise.allSettled([
          createNotification.mutateAsync({
            type: 'info',
            title: 'Welcome aboard!',
            message: 'We seeded your workspace with guided steps to help you launch faster.',
            metadata: { seeded: true },
          }),
          createNotification.mutateAsync({
            type: 'info',
            title: 'Weekly recaps',
            message: 'Toggle weekly recaps on the dashboard to receive Monday insights.',
            metadata: { seeded: true },
          }),
        ])

        const failedNotification = notificationResults.find(
          (result): result is PromiseRejectedResult => result.status === 'rejected'
        )
        if (failedNotification) {
          logger.warn('Skipped onboarding notification seed', {
            error:
              failedNotification.reason instanceof Error
                ? failedNotification.reason.message
                : String(failedNotification.reason),
          })
        }

        setState((prev) => ({
          ...prev,
          seededAt: new Date().toISOString(),
          lifecycle: {
            ...prev.lifecycle,
            welcomeSent: true,
          },
        }))
      } catch (error) {
        logger.error('Failed to seed onboarding demo data', error)
      }
    })().finally(() => {
      seededRef.current = false
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state,
    isAuthenticated,
    isSessionPending,
    createNotification.isPending,
    createNotification.mutateAsync,
  ])

  // Handle lifecycle emails
  useEffect(() => {
    if (!isAuthenticated) return
    if (!state.seededAt) return

    ;(async () => {
      const { trialEndsAt } = subscription

      // Send activation email when onboarding is complete
      if (shouldSendActivation(state)) {
        try {
          await createNotification.mutateAsync({
            type: 'success',
            title: 'Onboarding complete',
            message: 'Nice! You unlocked every item on the onboarding checklist.',
          })
        } catch (error) {
          logger.error('Failed to create activation notification', error)
        } finally {
          setState((prev) => ({
            ...prev,
            lifecycle: { ...prev.lifecycle, activationSent: true },
          }))
        }
      }

      if (shouldSendTrialEnding(state, trialEndsAt)) {
        try {
          await createNotification.mutateAsync({
            type: 'warning',
            title: 'Trial ending soon',
            message: `Your trial ends in ${
              trialEndsAt
                ? Math.ceil((new Date(trialEndsAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
                : 0
            } days. Add a payment method to keep your workspace active.`,
          })
        } catch (error) {
          logger.error('Failed to create trial reminder notification', error)
        } finally {
          setState((prev) => ({
            ...prev,
            lifecycle: {
              ...prev.lifecycle,
              trialEndingSent: true,
              trialReminderSentAt: new Date().toISOString(),
            },
          }))
        }
      }
    })()
  }, [state, createNotification, subscription, isAuthenticated])

  const tasksWithStatus = useMemo(
    () =>
      TASKS.map((task) => ({
        ...task,
        completed: state.completedTaskIds.includes(task.id),
      })),
    [state.completedTaskIds]
  )

  const progress = useMemo(() => {
    const completedCount = state.completedTaskIds.length
    return Math.round((completedCount / TASKS.length) * 100)
  }, [state.completedTaskIds.length])

  const isComplete = useMemo(() => {
    return state.completedTaskIds.length === TASKS.length
  }, [state.completedTaskIds.length])

  const completeTask = useCallback(
    async (taskId: string, options?: { silent?: boolean }) => {
      if (state.completedTaskIds.includes(taskId)) return

      const task = TASKS.find((t) => t.id === taskId)
      if (!task) return

      setState((prev) => ({
        ...prev,
        completedTaskIds: [...prev.completedTaskIds, taskId],
      }))

      if (!options?.silent && task.notification) {
        try {
          await createNotification.mutateAsync({
            type: task.notification.type,
            title: task.notification.title,
            message: task.notification.message,
          })
        } catch (error) {
          logger.error('Failed to create onboarding notification', error)
        }
      }

      trackEvent('onboarding_task_completed', {
        task_id: taskId,
        auto: false,
      })

      // Check if all tasks are complete
      const newCompletedCount = state.completedTaskIds.length + 1
      if (newCompletedCount === TASKS.length) {
        trackEvent('onboarding_completed', {
          total_tasks: TASKS.length,
        })
      }
    },
    [state.completedTaskIds, createNotification]
  )

  const markAutoComplete = useCallback(
    async (event: AutoCompleteEvent) => {
      const task = TASKS.find((t) => t.autoCompleteEvent === event)
      if (task) {
        await completeTask(task.id, { silent: true })
      }
    },
    [completeTask]
  )

  const contextValue: OnboardingContextValue = {
    tasks: tasksWithStatus,
    progress,
    isComplete,
    seededAt: state.seededAt,
    completeTask,
    markAutoComplete,
    state,
  }

  return <OnboardingContext.Provider value={contextValue}>{children}</OnboardingContext.Provider>
}

export function useOnboarding() {
  const context = useContext(OnboardingContext)
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider')
  }
  return context
}

// Helper functions (moved from lifecycle.ts for easier access)
function shouldSendActivation(state: OnboardingState): boolean {
  const allTasksCompleted = state.completedTaskIds.length === TASKS.length
  return allTasksCompleted && !state.lifecycle?.activationSent
}

function shouldSendTrialEnding(
  state: OnboardingState,
  trialEndsAt: string | Date | null | undefined
): boolean {
  if (!trialEndsAt || state.lifecycle?.trialEndingSent) return false

  const daysUntilTrialEnd = trialEndsAt
    ? Math.ceil((new Date(trialEndsAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : 0
  return daysUntilTrialEnd <= 3 && daysUntilTrialEnd >= 0
}
